/**
 * Color conversion utilities for ColorPicker (HSV ↔ HEX).
 * HSV: h ∈ [0,360), s ∈ [0,1], v ∈ [0,1]
 */

/**
 * Convert HSV to hex string (#RRGGBB).
 * @param {number} h - hue [0, 360)
 * @param {number} s - saturation [0, 1]
 * @param {number} v - value/brightness [0, 1]
 * @returns {string} e.g. "#FF6C22"
 */
export const hsvToHex = (h, s, v) => {
  const hi = Math.floor(h / 60) % 6;
  const f = h / 60 - Math.floor(h / 60);
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let r, g, b;

  switch (hi) {
    case 0: [r, g, b] = [v, t, p]; break;
    case 1: [r, g, b] = [q, v, p]; break;
    case 2: [r, g, b] = [p, v, t]; break;
    case 3: [r, g, b] = [p, q, v]; break;
    case 4: [r, g, b] = [t, p, v]; break;
    default: [r, g, b] = [v, p, q]; break;
  }

  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Convert hex string to HSV.
 * @param {string} hex - e.g. "#FF6C22" or "FF6C22"
 * @returns {{ h: number, s: number, v: number }}
 */
export const hexToHsv = (hex) => {
  const clean = hex.replace(/^#/, "");
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  const v = max;
  const s = max === 0 ? 0 : delta / max;

  let h = 0;

  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;

    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }

  return { h, s, v };
};

/**
 * Returns true if the hex string is a valid 6-char hex color.
 * @param {string} hex
 * @returns {boolean}
 */
export const isValidHex = (hex) => /^#[0-9A-Fa-f]{6}$/.test(hex);
